import moment from 'moment';
import { useContext, useMemo } from 'react';
import { CircularProgress } from '@mui/material';

import TableGrid from '../../components/table-grid';
import Divider from '../../components/v2/divider';
import InlineOptions from '../../components/v2/inline-options';
import { SnapshotTypeEnum } from '../../model/snapshot_type';
import { SnapshotDataTypeEnum } from '../../model/snapshot_data_type.enum';
import usePersistentState from '../../providers/use-persistent-state';
import { MarketChangeContext } from '../../providers/market-change/market-change-provider';
import {
  MarketChangeSurface,
  MarketChangeSurfaceContext,
  SURFACE_VOL_FIELDS,
  SurfaceGrid,
  SurfaceRow,
  selectSurfaceRows,
} from '../../providers/market-change/market-change-surface-provider';
import { formatTokenPrice } from '../../utils/decimal-map';

// IM-2598 - the Vol Surface Change hero panel. Three tiles over a shared row set
// (rows) x the PUT/CALL columns: S(T1), S(T2) and the signed change dS. Rendered
// with the platform TableGrid (ag-grid) and the SAME columns and value formatting
// the Market Data "Implied Vol Surface" panel uses
// (components/v2/widgets/volatility-surface), so the surfaces read identically to
// that widget: an Expiry/Tenor InlineOptions toggle (the SnapshotTypeEnum EE/CM
// snapshotType), the Expiry/Tenor first-column header, and - in CM - the same
// Utils.sortTenors ordering. The row set itself is chosen by selectSurfaceRows
// (shared by all three tiles); in EE it is driven by T2's expiries, with a new-in-
// T2 expiry shown faded. The ONLY style deviation is signed text colour on the dS
// cells (blue up / orange down, token-driven) to keep the direction of change
// legible, plus the token-driven muted styling on a faded row.

const fmtTimestamp = (ts?: string) =>
  ts ? `${moment(ts).utc().format('YYYY-MM-DD HH:mm')} UTC` : '';

// Vols display EXACTLY as the reference panel's percentFormat: value*100, 2dp,
// locale-grouped, with a '%'.
const percentFormat = (value: number): string =>
  parseFloat((value * 100).toFixed(2)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
  }) + '%';

// dS cells: the vol change in percentage points, signed, 2dp, with a '%' to match
// the vol-level grids (e.g. "+3.10%" / "-1.20%"). Round first, then guard negative
// zero so a tiny negative delta renders "0.00%", never "-0.00%"; the '+' is taken
// off the rounded value.
const formatDelta = (value: number): string => {
  const pts = Number((value * 100).toFixed(2));
  const signed = Object.is(pts, -0) ? 0 : pts;
  const sign = signed > 0 ? '+' : '';
  return `${sign}${signed.toFixed(2)}%`;
};

// Future-change cell: the signed futures price move, using the token's price
// formatting. Zero carries no sign.
const formatFutureDelta = (value: number, token: string): string => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatTokenPrice(Math.abs(value), token)}`;
};

// Token-driven muted styling on a faded (new-in-T2, EE-only) row's cells.
const fadedCellClass = (params: any): string[] =>
  params?.data?.__faded ? ['market-change__cell--faded'] : [];

// dS cells carry the faded class (when the row is faded) AND the signed-direction
// colour. Token-driven (the SCSS classes map to the chart blue/orange tokens);
// blank/zero cells stay the default text colour.
const deltaCellClass = (params: any): string[] => {
  const classes: string[] = [];
  if (params?.data?.__faded) classes.push('market-change__cell--faded');
  const value = params?.value;
  if (typeof value === 'string' && value !== '') {
    if (value.startsWith('+')) classes.push('market-change__ds-up');
    else if (value.startsWith('-')) classes.push('market-change__ds-down');
  }
  return classes;
};

// Display column subsets, mirroring the Implied Vol Surface widget's PUT/CALL and
// RR/FLY modes (ATMF appears in both). The full field set is retained and diffed in
// the provider (SURFACE_VOL_FIELDS); these pick which show as columns.
const PUT_CALL_FIELDS = ['10P', '25P', 'ATMF', '25C', '10C'];
const RR_FLY_FIELDS = ['ATMF', '25FLY', '25RR', '10FLY', '10RR'];

// Columns, order and headerNames mirror the reference getColumns: <Tenor|Expiry> |
// Future | <active vol fields>. The first-column header is 'Expiry' in EE and
// 'Tenor' in CM, exactly as the reference widget. Numeric columns right-aligned,
// nothing sortable.
const buildSurfaceColumns = (
  firstHeader: string,
  compact: boolean,
  fields: readonly string[],
): any[] => [
  {
    field: 'tenor',
    headerName: firstHeader,
    sortable: false,
    minWidth: compact ? 64 : 125,
    ...(compact ? { maxWidth: 96 } : {}),
    cellClass: fadedCellClass,
  },
  {
    field: 'Future',
    headerName: 'Future',
    sortable: false,
    minWidth: 110,
    align: 'right',
    cellClass: fadedCellClass,
  },
  ...fields.map((field) => ({
    field,
    headerName: field,
    sortable: false,
    align: 'right',
    cellClass: fadedCellClass,
  })),
];

// The Change table uses the same columns as the surfaces, including Future (shown
// as the signed futures move), plus the signed-direction colour cellClass.
const buildDeltaColumns = (
  firstHeader: string,
  compact: boolean,
  fields: readonly string[],
): any[] => [
  {
    field: 'tenor',
    headerName: firstHeader,
    sortable: false,
    minWidth: compact ? 64 : 125,
    ...(compact ? { maxWidth: 96 } : {}),
    cellClass: fadedCellClass,
  },
  {
    field: 'Future',
    headerName: 'Future',
    sortable: false,
    minWidth: 110,
    align: 'right',
    cellClass: deltaCellClass,
  },
  ...fields.map((field) => ({
    field,
    headerName: field,
    sortable: false,
    align: 'right',
    cellClass: deltaCellClass,
  })),
];

// One row per entry in the shared ordered row set. Future via formatTokenPrice,
// vols via percentFormat. A row whose key is absent from this surface (a faded,
// new-in-T2 expiry on the S(T1) side) carries no cells, so they render blank
// (never 0); __faded drives the muted cell styling.
const buildSurfaceRows = (
  rows: SurfaceRow[],
  surface: MarketChangeSurface | null,
  token: string,
): Record<string, any>[] =>
  rows.map(({ key, faded }) => {
    const row: Record<string, any> = { tenor: key, __faded: faded };
    const fields = surface?.tenors[key];
    if (fields) {
      if (typeof fields.Future === 'number') {
        row.Future = formatTokenPrice(fields.Future, token);
      }
      for (const field of SURFACE_VOL_FIELDS) {
        if (typeof fields[field] === 'number') {
          row[field] = percentFormat(fields[field]);
        }
      }
    }
    return row;
  });

// One row per entry in the shared ordered row set; the Future cell is the signed
// futures price move, each vol cell the signed percentage-point change. A faded
// row has no delta (no T1 to diff) so its cells render blank (never 0).
const buildDeltaRows = (
  rows: SurfaceRow[],
  delta: SurfaceGrid | null,
  token: string,
): Record<string, any>[] =>
  rows.map(({ key, faded }) => {
    const row: Record<string, any> = { tenor: key, __faded: faded };
    const fields = delta?.[key];
    if (fields) {
      if (typeof fields.Future === 'number') {
        row.Future = formatFutureDelta(fields.Future, token);
      }
      for (const field of SURFACE_VOL_FIELDS) {
        if (typeof fields[field] === 'number') {
          row[field] = formatDelta(fields[field]);
        }
      }
    }
    return row;
  });

interface SurfaceTileProps {
  title: string;
  subtitle?: string;
  columns: any[];
  rowData: Record<string, any>[];
  loading: boolean;
  className?: string;
}

const SurfaceTile = ({
  title,
  subtitle,
  columns,
  rowData,
  loading,
  className,
}: SurfaceTileProps) => {
  const hasData = rowData.length > 0;

  return (
    <div className="market-change__surface-tile">
      <div className="market-change__surface-tile-head">
        <span className="market-change__surface-tile-title">{title}</span>
        {subtitle && <span className="market-change__surface-tile-sub">{subtitle}</span>}
      </div>

      {loading && !hasData && (
        <div className="market-change__surface-state">
          <CircularProgress size={20} />
          Loading surface...
        </div>
      )}

      {!loading && !hasData && (
        <div className="market-change__surface-state">No data for this selection</div>
      )}

      {hasData && (
        <TableGrid
          columns={columns}
          rowData={rowData}
          className={className}
          autoHeight
          fitWidth
          suppressHorizontalScroll
        />
      )}
    </div>
  );
};

const VolSurfaceChangePanel = () => {
  const { surfaceT1, surfaceT2, surfaceDelta, snapshotType, setSnapshotType, loading, error } =
    useContext(MarketChangeSurfaceContext);
  const { market } = useContext(MarketChangeContext);

  const firstHeader = snapshotType === SnapshotTypeEnum.EE ? 'Expiry' : 'Tenor';
  // CM tenor labels are short (1D/1W/...), so the first column is compact; EE
  // expiry labels are longer and keep the wider first column.
  const compact = snapshotType === SnapshotTypeEnum.CM;

  // PUT/CALL vs RR/FLY selects which vol columns show (same data, different view),
  // mirroring the Implied Vol Surface widget. Persisted; no re-fetch.
  const [dataType, setDataType] = usePersistentState<SnapshotDataTypeEnum>(
    ['MARKET_CHANGE_SURFACE'],
    'dataType',
    SnapshotDataTypeEnum.PC,
  );
  const activeFields = dataType === SnapshotDataTypeEnum.RF ? RR_FLY_FIELDS : PUT_CALL_FIELDS;

  const surfaceColumns = useMemo(
    () => buildSurfaceColumns(firstHeader, compact, activeFields),
    [firstHeader, compact, activeFields],
  );
  const deltaColumns = useMemo(
    () => buildDeltaColumns(firstHeader, compact, activeFields),
    [firstHeader, compact, activeFields],
  );

  // One ordered row set drives all three tiles (see selectSurfaceRows).
  const rows = useMemo(
    () => selectSurfaceRows(surfaceT1, surfaceT2, snapshotType),
    [surfaceT1, surfaceT2, snapshotType],
  );

  const t1Rows = useMemo(
    () => buildSurfaceRows(rows, surfaceT1, market),
    [rows, surfaceT1, market],
  );
  const t2Rows = useMemo(
    () => buildSurfaceRows(rows, surfaceT2, market),
    [rows, surfaceT2, market],
  );
  const dsRows = useMemo(
    () => buildDeltaRows(rows, surfaceDelta, market),
    [rows, surfaceDelta, market],
  );

  return (
    <section className="market-change__panel market-change__panel--hero">
      <div className="market-change__surface-head">
        <h3 className="market-change__panel-title">Vol Surface Change</h3>
        <div className="market-change__surface-toggles">
          <InlineOptions
            options={[
              { value: SnapshotTypeEnum.EE, label: 'Expiry' },
              { value: SnapshotTypeEnum.CM, label: 'Tenor' },
            ]}
            selectedOption={snapshotType}
            onOptionChange={(val: string | string[]) => setSnapshotType(val as SnapshotTypeEnum)}
            optionDisabled={loading}
          />
          <Divider />
          <InlineOptions
            options={[
              { value: SnapshotDataTypeEnum.PC, label: 'PUT/CALL' },
              { value: SnapshotDataTypeEnum.RF, label: 'RR/FLY' },
            ]}
            selectedOption={dataType}
            onOptionChange={(val: string | string[]) => setDataType(val as SnapshotDataTypeEnum)}
          />
        </div>
      </div>
      {error && <p className="market-change__panel-error">{error}</p>}

      <div className="market-change__surface-tiles">
        <SurfaceTile
          title="First surface"
          subtitle={fmtTimestamp(surfaceT1?.timestamp)}
          columns={surfaceColumns}
          rowData={t1Rows}
          loading={loading}
        />
        <SurfaceTile
          title="Second surface"
          subtitle={fmtTimestamp(surfaceT2?.timestamp)}
          columns={surfaceColumns}
          rowData={t2Rows}
          loading={loading}
        />
        <SurfaceTile
          title="Change"
          subtitle="teal: up / orange: down"
          columns={deltaColumns}
          rowData={dsRows}
          loading={loading}
          className="market-change__ds-grid"
        />
      </div>
    </section>
  );
};

export default VolSurfaceChangePanel;
